//#region Imports

// Angular imports
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { UserService } from '../../../_services/data-services/user.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowReimbursementRequestService } from '../../_services/workflow-reimbursement-request.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';

// Model imports
import { WorkFlowParametersDto } from '../../../shared-workflow-module/_models/workflow-parameters-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';
import { BaseWorkflowSearchParameterDto } from '../../_models/workflow-reimbursement-request/base-workflow-search-parameter-dto';
import { BaseWorkflowSearchResultDto } from '../../_models/workflow-reimbursement-request/base-workflow-search-result-dto';

// Constant imports
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';
import { StringFormatConstants } from '../../../_constants/string-format-constants';

//#endregion

@Component({
    selector: 'app-workflow-reimbursement-request',
    templateUrl: './workflow-reimbursement-request.component.html',
    styleUrls: ['./workflow-reimbursement-request.component.scss']
})
export class WorkflowReimbursementRequestComponent implements OnInit {
    //#region Member Variables

    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    communityGridBoxValue: number[];
    communityId: number;
    managementCompanyId: number;

    actionOptions: any[];

    workflowReimbursements: BaseWorkflowSearchResultDto[] = [];
    selectedWorkflow: BaseWorkflowSearchResultDto;

    isInternalUser: boolean;

    selectedContext: PropertyCommunityInfoResultDto;

    showWorkFlowNotesPopUp = false;
    showWorkFlowHistoryPopUp = false;
    showWorkFlowBoardHistoryPopUp = false;
    showWorkFlowBoardNotesPopUp = false;

    showReimbursementEditor = false;
    reimbursementEditorTitle: string;

    loadingVisible = false;
    currencyFormat = StringFormatConstants.currencyFormat;

    iconRefresh = IconConstants.REFRESH;
    iconNotes = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes = IconConstants.NOTES_EMPTY;
    iconLock = IconConstants.LOCK;
    iconPDF = IconConstants.PDF_FILE_GRID;
    iconHistory = IconConstants.HISTORY;
    iconAdd = IconConstants.ADD;

    helpLink: string;
    baseWorkflowSearchParameterDto: BaseWorkflowSearchParameterDto;
    showWorkflowViewer = false;

    //#endregion

    //#region Component life cycle methods

    constructor(
        private readonly contextManagerService: ContextManagerService,
        private readonly communityService: CommunityService,
        private readonly errorService: ServerErrorService,
        private readonly workflowReimbursementService: WorkflowReimbursementRequestService,
        private readonly userService: UserService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly helperService: HelperService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly route: ActivatedRoute,
        private readonly precisionService: PrecisionService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.cbAPWFReimbursmentRequest);
        this.communityColumns = this.communityService.getCommunitySelectorColumns();
        this.isInternalUser = this.userService.isInternal();
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);

        this.route.queryParamMap.subscribe((qp) => {
            const uuid = qp.get('uuid');
            if (Utils.notNullAndDefined(uuid)) {
                const filters = localStorage.getItem(uuid);
                if (Utils.notNullAndDefined(filters)) {
                    const params = JSON.parse(filters);

                    this.managementCompanyId = params.managementCompanyId;
                    this.communityId = params.communityId;

                    this.getWorkflowReimbursements();

                    localStorage.removeItem(uuid);
                }

                window.history.replaceState({}, document.title, window.location.href.split('?')[0]);
            }
        });
    }
    ngOnInit(): void {
        this.selectedContext = this.contextManagerService.getSelectedContext();

        if (Utils.notNullAndDefined(this.selectedContext)) {
            // If we are in community context
            this.communityId = this.selectedContext.communityId;
            this.managementCompanyId = this.selectedContext.managementCompanyId;

            this.getWorkflowReimbursements();
        } else {
            this.getCommunityList();
        }
    }

    //#endregion

    //#region Event Handlers

    onCommunitySelection(selectedObject): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedCommunity: CommunitySelectorResultDto = selectedObject.selectedRow[0];

            this.communityId = selectedCommunity.communityId;
            this.managementCompanyId = selectedCommunity.managementCompanyId;

            this.getWorkflowReimbursements();
        } else {
            this.communityId = null;
            this.workflowReimbursements = [];
        }
    }

    onActionClick(record: BaseWorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showReimbursementEditor = true;
        this.reimbursementEditorTitle = `Edit Reimbursement AAP Workflow #${this.selectedWorkflow.workFlowId}`;
    }

    onNotesClick(record: BaseWorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowNotesPopUp = true;
    }

    onBoardNotesClick(record: BaseWorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowBoardNotesPopUp = true;
    }

    onHistoryClick(record: BaseWorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowHistoryPopUp = true;
    }

    onBoardHistoryClick(record: BaseWorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowBoardHistoryPopUp = true;
    }

    onDownloadFile(record: BaseWorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        const baseParams = new WorkFlowParametersDto();
        baseParams.workflowId = record.workFlowId;
        baseParams.communityId = record.communityId;

        this.downloadInvoice(baseParams);
    }

    onWorkflowNotesPopClose(): void {
        this.showWorkFlowNotesPopUp = false;
    }

    onWorkflowHistoryClose(): void {
        this.showWorkFlowHistoryPopUp = false;
    }

    onWorkflowBoardHistoryClose(): void {
        this.showWorkFlowBoardHistoryPopUp = false;
    }

    onWorkdlowBoardNotesPopClose(): void {
        this.showWorkFlowBoardNotesPopUp = false;
    }

    onNewReimbursementClick(): void {
        this.selectedWorkflow = null;
        this.showReimbursementEditor = true;
        this.reimbursementEditorTitle = 'Request New Reimbursement';
    }

    onReimbursementEdiorClose(event: boolean): void {
        if (Utils.isTrue(event)) {
            this.getWorkflowReimbursements();
        }

        this.showReimbursementEditor = false;
    }

    openPageInWindow(): void {
        const pageUrl = window.location.href.split('?')[0];
        const uuid = Utils.getUUID();
        localStorage.setItem(uuid, JSON.stringify(this.baseWorkflowSearchParameterDto));
        window.open(`${pageUrl}?uuid=${uuid}`, '_blank').focus();
    }

    onWorkflowClick(data: BaseWorkflowSearchResultDto): void {
        this.selectedWorkflow = data;
        this.showWorkflowViewer = true;
    }

    //#endregion

    //#region Private methods

    private getWorkflowReimbursements(): void {
        const baseParam: BaseWorkflowSearchParameterDto = new BaseWorkflowSearchParameterDto();
        baseParam.managementCompanyId = this.managementCompanyId;
        baseParam.communityId = this.communityId;

        this.baseWorkflowSearchParameterDto = baseParam;

        this.loadingVisible = true;
        this.workflowReimbursementService.getWorflowReimbursements(baseParam).subscribe(
            (result: BaseWorkflowSearchResultDto[]) => {
                this.workflowReimbursements = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunityList() {
        this.loadingVisible = true;

        this.communityService.getCommunities().subscribe(
            (data) => {
                this.communityList = data;
                this.communityGridBoxValue = Utils.notNullAndDefined(this.communityId) ? [this.communityId] : null;

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private downloadInvoice(baseParams: WorkFlowParametersDto): void {
        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                window.open(fileURL, '_blank', 'location=yes, scrollbars=yes,status=yes');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    //#endregion
}
